import { TUpsertContextProductPayload } from 'features/context-ids/types/types';

export type SyncWithMasterContextTarget = {
  tenantId: string;
  tenantName: string;
  environment: TUpsertContextProductPayload['environment'];
  environmentLabel: string;
};

export type SyncWithMasterContextPlan = {
  target: SyncWithMasterContextTarget | null;
  disabledReason: string | null;
};

type ResourceForSync = {
  id?: string;
  type?: string;
  tenantId: string;
  tenantName: string;
  tenantProductCode?: string;
};

const trimId = (value: string | undefined) => (value ?? '').trim();

const getEdgeResources = (contextResources: ResourceForSync[]) =>
  contextResources.filter((r) => r.type?.toLowerCase() === 'edge');

// The API payload requires the XMCloud/SitecoreAI tenant ID, which is shared
// by non-edge XMCloud resources (forms, files, personalize, xm-authoring).
// The edge resource has its own platform-specific tenant ID that is different.
const getXMCloudTenantResource = (contextResources: ResourceForSync[]) =>
  contextResources.find(
    (r) =>
      (r.tenantProductCode ?? '').toLowerCase() === 'xmcloud' &&
      (r.type ?? '').toLowerCase() !== 'edge',
  );

export const getSyncWithMasterContextPlan = (
  contextResources: ResourceForSync[],
): SyncWithMasterContextPlan => {
  const edges = getEdgeResources(contextResources);
  const liveEdge = edges.find((r) => r.tenantProductCode === 'Edge');
  const previewEdge = edges.find((r) => r.tenantProductCode !== 'Edge');

  // Get the XMCloud tenant ID from a non-edge resource (forms, files, personalize, etc.)
  // Edge resources determine which environments to sync, not the tenantId to use.
  const xmCloudTenantResource = getXMCloudTenantResource(contextResources);
  const xmCloudTenantId = trimId(xmCloudTenantResource?.tenantId);
  const xmCloudTenantName =
    xmCloudTenantResource?.tenantName || xmCloudTenantId;

  if (xmCloudTenantId) {
    if (liveEdge) {
      return {
        target: {
          tenantId: xmCloudTenantId,
          tenantName: xmCloudTenantName,
          environment: 'Live',
          environmentLabel: 'Live',
        },
        disabledReason: null,
      };
    }
    if (previewEdge) {
      return {
        target: {
          tenantId: xmCloudTenantId,
          tenantName: xmCloudTenantName,
          environment: 'Preview',
          environmentLabel: 'Preview',
        },
        disabledReason: null,
      };
    }
  }

  const disabledReason =
    edges.length === 0
      ? 'Add an Edge (SitecoreAI) resource to this context before you can sync with the master context.'
      : 'This context is missing a SitecoreAI tenant ID. Ensure it has SitecoreAI resources (such as Forms or Files) and try again.';

  return { target: null, disabledReason };
};
